import { define, html, router, store } from "hybrids";

import layout from "~/factories/layout";
import socketio from "~/factories/socketio";

import ArtistVideo, { toggleVideoFavorite } from "~/stores/ArtistVideo";
import Comment from "~/stores/Comment";
import PaymentMethods from "~/stores/PaymentMethods";

import { VBroadcastContainer } from "~/modules/broadcast/elements/Container";

import { Socket } from "socket.io-client";

import Donate from "./donate/Root";
import Share from "./Share";
import People from "./People";
import User from "./User";
import Chat from "./Chat";
import Session from "~/stores/Session";
import { redirectToSignIn } from "~/utils/redirections";

export interface VVideoRoot extends HTMLElement {
  video?: ArtistVideo;
  payments: PaymentMethods;
  views: HTMLElement[];
  session: Session;
  socketUrl: string;
  accessToken: string;
  socket: Socket | null;
  unreadComments: boolean;
  autoLike: boolean;

  videoId: string;
}

function handleChatClick(host: VVideoRoot, event: Event) {
  host.unreadComments = false;

  if (router.active(Chat)) {
    event.preventDefault();

    const el = host.querySelector(
      "v-broadcast-container[stick-to-bottom]"
    ) as VBroadcastContainer;

    el?.scrollToBottom();
  }
}

const toggleFavoriteHandler = async (host: VVideoRoot, event: Event) => {
  try {
    const session = await store.resolve(Session);
    toggleVideoFavorite(host.video);
  } catch (error) {
    redirectToSignIn("autoLike=true");
  }
}

export default define<VVideoRoot>({
  tag: "v-video-root",
  video: store(ArtistVideo),
  payments: store(PaymentMethods, {
    id: ({ video }) => (store.ready(video) ? video?.owner?.id : undefined),
  }),
  videoId: ({ video }) => `/watch/${video?.id}`,
  session: store(Session),
  views: router([People, Chat], { params: ["video"] }),
  unreadComments: false,
  socketUrl: "",
  accessToken: "",
  autoLike: {
    value: false,
    observe({ video }, value) {
      if (value && !video?.hasLike) {
        toggleVideoFavorite(video);
      }
    }
  },
  render: layout("flex"),
  socket: socketio(async (host, socket) => {
    socket.on("comments:create", async (comment: Comment) => {
      console.log("socket event: 'comments:create'");
      const video = await store.resolve(host.video);
      const session = await store.resolve(host.session);

      if (session.blockedUsers.find((u) => u == comment.user)) {
        return;
      }

      if (video && !video.comments.find((c) => c.id === comment.id)) {
        store.sync(video, {
          comments: [comment].concat(video.comments),
        });

        if (!router.active(Chat, { stack: true })) {
          host.unreadComments = true;
        }
      }
    });

    socket.on("comments:update", async (values: Comment) => {
      console.log("socket event: 'comments:update'");
      const comment = store.get(Comment, values.id);

      if (store.ready(comment)) {
        const { id, ...fields } = values;
        store.sync(comment, fields);
      }
    });

    socket.on("comments:delete", async (values: Comment) => {
      console.log("socket event: 'comments:delete'");
      const video = await store.resolve(host.video);
      const comment = store.get(Comment, values.id);

      if (video && store.ready(comment)) {
        store.sync(video, {
          comments: video.comments.filter(
            (c) => c !== comment && c.parent !== comment
          ),
        });
        store.sync(comment, null);
      }
    });

    socket.on("connect_error", console.error);
  }),
  content: ({
    video,
    payments,
    views,
    unreadComments,
    session,
    videoId,
  }) => html`
    <v-broadcast-layout>
      ${video &&
      store.ready(video) &&
      html`
        <v-broadcast-player
          slot="player"
          src="${video.src}"
          iframelyKey="${video.iframely}"
          muxDataApiKey="${video.muxDataApiKey}"
          videoId="${videoId}"
          videoTitle="${video.name}"
          viewerUserId="${store.ready(session) && session.user.id}"
        ></v-broadcast-player>

        ${(window.innerWidth > 921 || !router.active(Chat)) && html`
          <v-ui-text variant="h1" selectable slot="title">
            ${video.name}
          </v-ui-text>

          <v-ui-flexbox flow="row" slot="title">
            <v-ui-button
              name="Favorite"
              direction="column"
              variant="icon"
              icon=${video.hasLike ? "heartfill" : "heart"}
              iconcolor=${video.hasLike ? "primary" : "icon"}
              justify="right"
              active
              onclick=${toggleFavoriteHandler}
              id="favorites"
            >
            </v-ui-button>
            <v-ui-button
              href="${router.url(Share)}"
              variant="icon"
              icon="share"
              iconcolor="icon"
              iconsize="medium"
              name="Share"
              justify="right"
              active
            ></v-ui-button>
            ${video.permissions.canManageAllComments &&
            html`
              <v-ui-button
                variant="icon"
                icon="edit"
                iconcolor="icon"
                href="/manage/${video.owner}/videos/edit/${video.id}"
                name="Edit"
                justify="right"
                active
              ></v-ui-button>`
            }
          </v-ui-flexbox>`}

        ${video.sponsors.map(
          (sponsor) =>
            html`
              <v-ui-link
                href="${router.url(User, { user: sponsor.profile })}"
                block
                slot="sponsors"
              >
                <v-broadcast-sponsor-logo
                  src="${sponsor.image}"
                  alt="${sponsor.name}"
                ></v-broadcast-sponsor-logo>
              </v-ui-link>
            `
        )}

        <v-ui-flexbox flow="row" grow="1">${views}</v-ui-flexbox>
      `}

      <div>
        ${store.ready(video) &&
        !router.active([Donate]) &&
        html`
          <v-broadcast-actions shadow="${router.active(People)}">
            ${store.ready(payments) &&
            !payments.disabled &&
            html`
              <v-ui-button
                href="${router.url(Donate)}"
                icon="donate"
                name="Donate"
                color="primary"
              ></v-ui-button>`}
            <v-ui-button
              href="${router.url(People, { video })}"
              variant="icon"
              icon="info"
              name="Info"
              active="${router.active(People)}"
              slot="info-chat"
            ></v-ui-button>
            <v-ui-button
              href="${router.url(Chat, { video })}"
              variant="icon"
              icon="chat"
              name="Chat"
              active="${router.active(Chat)}"
              dot="${unreadComments}"
              onclick="${handleChatClick}"
              slot="info-chat"
            ></v-ui-button>
          </v-broadcast-actions>
        `}
      </div>
    </v-broadcast-layout>
  `,
});
